import { ChangeEvent, useCallback, useMemo, useState } from 'react';
import { useLocation, useParams, useSearchParams } from 'react-router';
import {
  useExternalConsentsDataQuery,
  usePSD2OfflineChooseBankMutation,
  usePSD2OfflineClientConsentsQuery,
  usePSD2OfflineConfirmConsentsMutation,
  usePSD2ProxyClientConsentsQuery,
  usePSD2ProxyConfirmConsentsMutation,
} from 'hooks/webapi';
import useRedirect from 'hooks/webapi/useRedirect';
import { MANUAL_PSD2_CONSENTS, OFFLINE_PSD2_CONSENTS } from 'Router/routes';
import { Consent } from 'hooks/webapi/external/useConsentsQuery';
import useError from 'hooks/useError';

export default function usePSD2Consents() {
  const redirect = useRedirect();
  const location = useLocation();
  const params = useParams();
  const { showError } = useError();
  const [searchParams] = useSearchParams();
  const isOffline = location.pathname.includes(OFFLINE_PSD2_CONSENTS);
  const isManual = location.pathname.includes(MANUAL_PSD2_CONSENTS);

  const [consentAccepted, setConsentAccepted] = useState(false);

  // This returns all the available consents with all the metadata and actual content
  const { data: availableConsents, isLoading: availableConsentsIsLoading } =
    useExternalConsentsDataQuery();

  // This returns consentType we need
  const { data: consentTypeData, isLoading: consentTypeDataIsLoading } =
    usePSD2ProxyClientConsentsQuery(!isOffline);
  const {
    data: offlineConsentTypeData,
    isLoading: offlineConsentTypeDataIsLoading,
  } = usePSD2OfflineClientConsentsQuery(params.token, isOffline);

  const {
    mutateAsync: confirmConsentAndAuthorizePSD2Client,
    isPending: isConfirmConsentAndAuthorizePSD2ClientPending,
  } = usePSD2ProxyConfirmConsentsMutation();

  const {
    mutateAsync: offlineConfirmConsentAndAuthorizePSD2Client,
    isPending: offlineIsPending,
  } = usePSD2OfflineConfirmConsentsMutation();

  const { mutateAsync: chooseBank } = usePSD2OfflineChooseBankMutation();

  const handleConsentChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setConsentAccepted(Boolean(e.target.checked));
    },
    [],
  );

  const consentHTMLContent = useMemo(() => {
    if (!availableConsents) {
      return '';
    }

    let consent: Consent | undefined;

    if (isOffline && offlineConsentTypeData) {
      consent = availableConsents[offlineConsentTypeData.consentType];
    }

    if (!isOffline && consentTypeData) {
      consent = availableConsents[consentTypeData.consentType];
    }

    // If the consent for this type is not available, we can't proceed
    if (!consent) {
      return '';
    }

    if (consent.content.length > 0) {
      return consent.content[0].text;
    }

    return '';
  }, [consentTypeData, availableConsents, offlineConsentTypeData]);

  const confirm = async () => {
    let response;

    if (isOffline) {
      if (!params.token) {
        showError(new Error('V URL chybí token. Zavřete okno a zkuste znovu.'));
        return;
      }

      const bankId = searchParams.get('bankId');

      if (!bankId) {
        showError(
          new Error('V URL chybí bankId. Zavřete okno a zkuste znovu.'),
        );
        return;
      }

      await chooseBank({
        token: params.token,
        bankCode: bankId,
      });

      response = await offlineConfirmConsentAndAuthorizePSD2Client({
        token: params.token,
        consent: consentAccepted,
      });
    } else {
      response = await confirmConsentAndAuthorizePSD2Client({
        consent: consentAccepted,
      });
    }

    // Add token for offline redirection
    const resultUrl = isOffline
      ? `${response.url}/${params.token}`
      : response.url;

    redirect(resultUrl);
  };

  return {
    confirm,
    consentAccepted,
    handleConsentChange,
    isPending:
      isConfirmConsentAndAuthorizePSD2ClientPending || offlineIsPending,
    isLoading:
      availableConsentsIsLoading ||
      (isOffline && offlineConsentTypeDataIsLoading) ||
      (!isOffline && consentTypeDataIsLoading),
    consentHTMLContent,
    isManual,
  };
}
